import { api } from './apiClient';
import { isPathwaysUrl } from './urlUtils';

interface LaunchableProduct {
  code?: string;
  accessUrl?: string | null;
}

const POLLS_PRODUCT_CODE = 'polls';

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Polls has no portal base-URL config, so its origin comes from the user's own
 * Polls product accessUrl — the same URL its product card launches.
 */
function isPollsUrl(url: string, products: LaunchableProduct[]): boolean {
  const origin = originOf(url);
  if (!origin) return false;

  const pollsOrigins = new Set(
    products
      .filter(({ code, accessUrl }) => code === POLLS_PRODUCT_CODE && accessUrl)
      .map(({ accessUrl }) => originOf(accessUrl as string))
  );

  return pollsOrigins.size === 1 && pollsOrigins.has(origin);
}

/** The product whose access token a return URL should carry, if any. */
export function productCodeForUrl(url: string, products: LaunchableProduct[] = []): string | null {
  if (isPathwaysUrl(url)) return 'pathways';
  if (isPollsUrl(url, products)) return POLLS_PRODUCT_CODE;
  return null;
}

/**
 * Appends a product access token for Pathways and Polls URLs; other URLs are
 * returned unchanged. Null means minting failed.
 * Callers must have already validated the URL with isValidReturnUrl.
 */
export async function getRedirectUrlWithToken(
  url: string,
  products?: LaunchableProduct[]
): Promise<string | null> {
  const code = productCodeForUrl(url, products);
  if (!code) return url;

  try {
    const response = await api.createAccessTokenForProduct(code);
    const token = response?.data?.attributes?.accessToken;

    if (token) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('access', token);
      return urlObj.toString();
    }
  } catch (error) {
    console.error(`Error generating access token for ${code}:`, error);
  }

  return null;
}
